import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bell, ArrowRight, CheckCheck } from 'lucide-react';
import api from '../lib/api';

/* Navbar bell: every in-app notification (teacher reviews, class reminders, results,
   broadcasts…). Clicking an item marks it read and navigates to its destination.
   Re-fetches on every route change so the badge stays honest. */
export const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
};

export default function NotificationBell() {
  const [feed, setFeed] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/notifications', { params: { limit: 8 } }).then((r) => setFeed(r.data || { items: [], unread: 0 })).catch(() => {});
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    setFeed((f) => {
      const unseen = f.items.filter((x) => !x.seen_at && !x.read_at).map((x) => x.id);
      if (!unseen.length) return f;
      // Opening the bell = the student saw these; badge clears, items stay highlighted until clicked.
      api.post('/api/notifications/seen', { ids: unseen }).catch(() => {});
      return { ...f, unread: 0, items: f.items.map((x) => (unseen.includes(x.id) ? { ...x, seen_at: new Date().toISOString() } : x)) };
    });
    const close = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const openItem = async (it) => {
    setOpen(false);
    if (it.unread) {
      setFeed((f) => ({ unread: Math.max(0, f.unread - 1), items: f.items.map((x) => (x.id === it.id ? { ...x, unread: false, seen_at: x.seen_at || new Date().toISOString() } : x)) }));
      api.post('/api/notifications/read', { ids: [it.id] }).catch(() => {});
    }
    if (/^https?:\/\//i.test(it.link)) window.open(it.link, '_blank', 'noopener'); else navigate(it.link || '/dashboard');
  };
  const markAll = async () => {
    setFeed((f) => ({ unread: 0, items: f.items.map((x) => ({ ...x, unread: false })) }));
    api.post('/api/notifications/read', { all: true }).catch(() => {});
  };

  return (
    <div className="relative" ref={wrap}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-label={feed.unread ? `${feed.unread} unread notifications` : 'Notifications'}
        className="relative w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600 transition-colors" data-testid="notification-bell">
        <Bell size={18} className={feed.unread ? 'text-amber-600' : ''} />
        {feed.unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white" data-testid="notification-bell-count">{feed.unread > 9 ? '9+' : feed.unread}</span>
        )}
      </button>
      {open && (
        <div className="fixed inset-x-3 top-[68px] sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2 sm:w-[360px] bg-white rounded-2xl shadow-xl border border-slate-100 z-50 animate-scale-in overflow-hidden" data-testid="notification-dropdown">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-900">Notifications{feed.unread > 0 && <span className="ml-2 text-[11px] font-bold text-rose-600" data-testid="notification-unread-label">{feed.unread} new</span>}</span>
            {feed.unread > 0 && <button type="button" onClick={markAll} className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800" data-testid="notification-mark-all"><CheckCheck size={12} /> Mark all read</button>}
          </div>
          {feed.items.length === 0 ? (
            <p className="px-4 py-6 text-sm text-slate-500 text-center" data-testid="notification-empty">You're all caught up. Class reminders, results and teacher feedback will show up here.</p>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-[380px] overflow-y-auto">
              {feed.items.map((it) => (
                <li key={it.id}>
                  <button type="button" onClick={() => openItem(it)} className={`w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-amber-50/60 transition-colors ${it.unread ? 'bg-amber-50/40' : ''}`} data-testid={`notification-item-${it.id}`}>
                    <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${it.unread ? 'bg-rose-500' : 'bg-slate-200'}`} />
                    <span className="flex-1 min-w-0">
                      <span className={`block text-sm truncate ${it.unread ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>{it.title}</span>
                      <span className="block text-xs text-slate-500 mt-0.5 line-clamp-2">{it.body}</span>
                      <span className="block text-[11px] text-slate-400 mt-1">{timeAgo(it.created_at)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Link to="/notifications" onClick={() => setOpen(false)} className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors border-t border-amber-100" data-testid="notification-view-all">
            See all notifications <ArrowRight size={12} />
          </Link>
        </div>
      )}
    </div>
  );
}
